import React, { useState, useEffect } from 'react';
import { Building2, Briefcase, Users, TrendingUp, Calendar, ChevronRight, Loader2 } from 'lucide-react';
import axios from 'axios';
import { Link } from 'react-router-dom';
import { formatDate } from '../../utils/dateTimeUtils';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const PlacementDashboard = () => {
  const [dashboard, setDashboard] = useState(null);
  const [loading, setLoading] = useState(true);
  const token = localStorage.getItem('token');

  useEffect(() => {
    fetchDashboard();
  }, []);

  const fetchDashboard = async () => {
    try {
      const response = await axios.get(`${API_URL}/api/placement/analytics/dashboard`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setDashboard(response.data);
    } catch (error) {
      console.error('Error fetching placement dashboard:', error);
    } finally {
      setLoading(false);
    }
  };

  const getStatusColor = (status) => {
    const colors = {
      applied: 'bg-blue-100 text-blue-800',
      shortlisted: 'bg-yellow-100 text-yellow-800',
      interview_scheduled: 'bg-purple-100 text-purple-800',
      selected: 'bg-green-100 text-green-800',
      rejected: 'bg-red-100 text-red-800',
      offer_accepted: 'bg-emerald-100 text-emerald-800'
    };
    return colors[status] || 'bg-gray-100 text-gray-800';
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <Loader2 className="h-8 w-8 animate-spin text-blue-600" />
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="placement-dashboard">
      <div className="flex justify-between items-center">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Placement Cell</h1>
          <p className="text-sm text-gray-500 mt-1">Manage campus placements and recruitment</p>
        </div>
      </div>

      {/* Summary Cards */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
        <div className="bg-gradient-to-br from-blue-500 to-blue-600 p-6 rounded-xl text-white">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-blue-100 text-sm">Total Companies</p>
              <p className="text-3xl font-bold mt-1">{dashboard?.summary?.total_companies || 0}</p>
            </div>
            <Building2 className="h-12 w-12 text-blue-200" />
          </div>
          <Link to="companies" className="mt-4 text-sm text-blue-100 hover:text-white flex items-center">
            View Companies <ChevronRight className="h-4 w-4 ml-1" />
          </Link>
        </div>

        <div className="bg-gradient-to-br from-purple-500 to-purple-600 p-6 rounded-xl text-white">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-purple-100 text-sm">Active Jobs</p>
              <p className="text-3xl font-bold mt-1">{dashboard?.summary?.active_jobs || 0}</p>
            </div>
            <Briefcase className="h-12 w-12 text-purple-200" />
          </div>
          <Link to="jobs" className="mt-4 text-sm text-purple-100 hover:text-white flex items-center">
            View Jobs <ChevronRight className="h-4 w-4 ml-1" />
          </Link>
        </div>

        <div className="bg-gradient-to-br from-orange-500 to-orange-600 p-6 rounded-xl text-white">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-orange-100 text-sm">Total Applications</p>
              <p className="text-3xl font-bold mt-1">{dashboard?.summary?.total_applications || 0}</p>
            </div>
            <Users className="h-12 w-12 text-orange-200" />
          </div>
          <Link to="applications" className="mt-4 text-sm text-orange-100 hover:text-white flex items-center">
            View Applications <ChevronRight className="h-4 w-4 ml-1" />
          </Link>
        </div>

        <div className="bg-gradient-to-br from-green-500 to-green-600 p-6 rounded-xl text-white">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-green-100 text-sm">Students Placed</p>
              <p className="text-3xl font-bold mt-1">{dashboard?.summary?.students_placed || 0}</p>
              <p className="text-green-200 text-xs mt-1">{dashboard?.summary?.placement_rate || 0}% rate</p>
            </div>
            <TrendingUp className="h-12 w-12 text-green-200" />
          </div>
          <Link to="reports" className="mt-4 text-sm text-green-100 hover:text-white flex items-center">
            View Reports <ChevronRight className="h-4 w-4 ml-1" />
          </Link>
        </div>
      </div>

      {/* Status Breakdown */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <div className="bg-white rounded-xl shadow p-6">
          <h3 className="text-lg font-semibold mb-4">Application Status Overview</h3>
          <div className="space-y-3">
            {Object.entries(dashboard?.status_breakdown || {}).map(([status, count]) => (
              <div key={status} className="flex items-center justify-between">
                <span className={`px-3 py-1 rounded-full text-sm font-medium ${getStatusColor(status)}`}>
                  {status.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase())}
                </span>
                <span className="font-semibold text-gray-900">{count}</span>
              </div>
            ))}
          </div>
        </div>

        <div className="bg-white rounded-xl shadow p-6">
          <h3 className="text-lg font-semibold mb-4">Upcoming Company Visits</h3>
          {dashboard?.upcoming_visits?.length > 0 ? (
            <div className="space-y-3">
              {dashboard.upcoming_visits.map(visit => (
                <div key={visit.id} className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
                  <div>
                    <p className="font-medium text-gray-900">{visit.company_name}</p>
                    <p className="text-sm text-gray-500">{visit.purpose}</p>
                  </div>
                  <div className="text-right">
                    <p className="text-sm font-medium text-blue-600">{visit.visit_date}</p>
                    <p className="text-xs text-gray-500">{visit.visit_time}</p>
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <p className="text-gray-500 text-center py-8">No upcoming visits scheduled</p>
          )}
          <Link to="visits" className="mt-4 block text-center text-sm text-blue-600 hover:text-blue-800">
            View All Visits →
          </Link>
        </div>
      </div>

      {/* Recent Applications */}
      <div className="bg-white rounded-xl shadow">
        <div className="p-6 border-b">
          <h3 className="text-lg font-semibold">Recent Applications</h3>
        </div>
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Student</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Job</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Company</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Status</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Applied</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-200">
              {dashboard?.recent_applications?.length > 0 ? (
                dashboard.recent_applications.map(app => (
                  <tr key={app.id} className="hover:bg-gray-50">
                    <td className="px-6 py-4">
                      <p className="font-medium text-gray-900">{app.student_name}</p>
                      <p className="text-sm text-gray-500">{app.admission_number}</p>
                    </td>
                    <td className="px-6 py-4 text-sm text-gray-900">{app.job_title}</td>
                    <td className="px-6 py-4 text-sm text-gray-900">{app.company_name}</td>
                    <td className="px-6 py-4">
                      <span className={`px-2 py-1 rounded-full text-xs font-medium ${getStatusColor(app.status)}`}>
                        {app.status.replace(/_/g, ' ')}
                      </span>
                    </td>
                    <td className="px-6 py-4 text-sm text-gray-500">
                      {formatDate(app.applied_at)}
                    </td>
                  </tr>
                ))
              ) : (
                <tr>
                  <td colSpan="5" className="px-6 py-8 text-center text-gray-500">
                    No applications yet
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
};

export default PlacementDashboard;
